import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const vettureChildren = [
  { to: "/vetture?stato=disponibili", it: "Disponibili ora", en: "Available now", id: "nav-vetture-disponibili" },
  { to: "/vendi", it: "Vendi la tua", en: "Sell yours", id: "nav-vetture-vendi" },
  { to: "/vendute", it: "Vendute", en: "Sold", id: "nav-vetture-vendute" },
];

const links = [
  { to: "/", it: "Home", en: "Home", id: "nav-home" },
  { to: "/vetture", it: "Vetture", en: "Cars", id: "nav-vetture", children: vettureChildren },
  { to: "/#chi-sono", it: "Chi sono", en: "About", id: "nav-chi-sono" },
];

function isInternal(to) {
  return to.startsWith("/") && !to.includes("#");
}

function LangSwitcher({ className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center gap-1 label-eyebrow ${className}`} data-testid="lang-switcher">
      <button
        onClick={() => setLang("it")}
        className={`px-1 transition-colors duration-300 ${lang === "it" ? "text-white" : "text-neutral-500 hover:text-neutral-300"}`}
        data-testid="lang-it"
      >
        IT
      </button>
      <span className="text-neutral-700">/</span>
      <button
        onClick={() => setLang("en")}
        className={`px-1 transition-colors duration-300 ${lang === "en" ? "text-white" : "text-neutral-500 hover:text-neutral-300"}`}
        data-testid="lang-en"
      >
        EN
      </button>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [vettureOpen, setVettureOpen] = useState(false);
  const loc = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setVettureOpen(false); }, [loc.pathname, loc.search, loc.hash]);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 1.2, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 sm:px-10 lg:px-14 py-5">
        <Link to="/" data-testid="brand-logo" className="group" aria-label="Davide Perugini Supercar & Classic Cars — Home">
          <div className="flex items-baseline gap-2.5">
            <span className="font-serif text-xl sm:text-2xl tracking-tight text-white leading-none">Davide Perugini</span>
            <span className="label-eyebrow hidden sm:inline text-neutral-500 group-hover:text-neutral-300 transition-colors">
              Supercar &amp; Classic Cars
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.children ? (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={() => setVettureOpen(true)}
                onMouseLeave={() => setVettureOpen(false)}
              >
                <Link
                  to={l.to}
                  data-testid={l.id}
                  className="label-eyebrow text-neutral-400 hover:text-white transition-colors duration-500 inline-flex items-center gap-1.5"
                >
                  {l[lang]}
                  <ChevronDown size={13} className={`transition-transform duration-300 ${vettureOpen ? "rotate-180" : ""}`} />
                </Link>
                <AnimatePresence>
                  {vettureOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-5"
                      data-testid="vetture-dropdown"
                    >
                      <div className="min-w-[240px] bg-black/95 backdrop-blur-xl border border-white/10 p-2 shadow-2xl">
                        {l.children.map((c) =>
                          isInternal(c.to) ? (
                            <Link
                              key={c.id}
                              to={c.to}
                              data-testid={c.id}
                              className="block px-5 py-4 label-eyebrow text-neutral-400 hover:text-white hover:bg-white/5 transition-colors duration-300"
                            >
                              {c[lang]}
                            </Link>
                          ) : (
                            <a
                              key={c.id}
                              href={c.to}
                              data-testid={c.id}
                              className="block px-5 py-4 label-eyebrow text-neutral-400 hover:text-white hover:bg-white/5 transition-colors duration-300"
                            >
                              {c[lang]}
                            </a>
                          )
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <a
                key={l.to}
                href={l.to}
                data-testid={l.id}
                className="label-eyebrow text-neutral-400 hover:text-white transition-colors duration-500"
              >
                {l[lang]}
              </a>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <LangSwitcher />
          <a
            href="#contatti"
            data-testid="header-cta-contatti"
            className="btn-magnetic"
          >
            {lang === "en" ? "Contact me" : "Contattami"}
          </a>
        </div>

        <div className="md:hidden flex items-center gap-4">
          <LangSwitcher />
          <button
            className="text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="md:hidden overflow-hidden bg-black border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              {links.map((l) => (
                <div key={l.to}>
                  {isInternal(l.to) ? (
                    <Link to={l.to} className="text-2xl font-serif text-white" data-testid={`mobile-${l.id}`}>
                      {l[lang]}
                    </Link>
                  ) : (
                    <a href={l.to} className="text-2xl font-serif text-white" data-testid={`mobile-${l.id}`}>
                      {l[lang]}
                    </a>
                  )}
                  {l.children && (
                    <div className="flex flex-col gap-3 mt-4 pl-4 border-l border-white/10">
                      {l.children.map((c) =>
                        isInternal(c.to) ? (
                          <Link key={c.id} to={c.to} className="text-base text-neutral-400" data-testid={`mobile-${c.id}`}>
                            {c[lang]}
                          </Link>
                        ) : (
                          <a key={c.id} href={c.to} className="text-base text-neutral-400" data-testid={`mobile-${c.id}`}>
                            {c[lang]}
                          </a>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
